// components/reports/DriftActivityChart.js
// Drift Activity — Recharts BarChart showing drift events per week
// Stacked by classification: negative (red), neutral (amber), positive (green)
// Uses useThemeColors() for all colors — no CSS var strings in SVG props

import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { useThemeColors } from '../../hooks/useThemeColors';

function DriftActivityChart({ data = [] }) {
  const colors = useThemeColors();

  if (!data || data.length === 0) {
    return (
      <div className="trend-chart-card">
        <h3 className="trend-chart-title">Drift Activity</h3>
        <div className="trend-chart-empty">
          No drift activity data available yet.
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    const total = (d.negative || 0) + (d.neutral || 0) + (d.positive || 0);

    return (
      <div style={{
        background: colors.bg.card,
        border: `1px solid ${colors.border.medium}`,
        borderRadius: '6px',
        padding: '10px 14px',
        fontSize: '13px'
      }}>
        <div style={{ color: colors.text.primary, fontWeight: 'bold', marginBottom: 6 }}>
          {d.label || label} — {total} events
        </div>
        <div style={{ color: colors.severity.critical }}>Negative: {d.negative || 0}</div>
        <div style={{ color: colors.severity.medium }}>Neutral: {d.neutral || 0}</div>
        <div style={{ color: colors.severity.low }}>Positive: {d.positive || 0}</div>
      </div>
    );
  };

  return (
    <div className="trend-chart-card">
      <div className="trend-chart-header">
        <h3 className="trend-chart-title">Drift Activity</h3>
        <span className="trend-chart-period">Last {data.length} weeks</span>
      </div>
      <div className="trend-chart-body">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.border.dark} />
            <XAxis
              dataKey="label"
              stroke={colors.text.muted}
              tick={{ fontSize: 12, fill: colors.text.muted }}
            />
            <YAxis
              stroke={colors.text.muted}
              tick={{ fontSize: 12, fill: colors.text.muted }}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="negative"
              stackId="drift"
              fill={colors.severity.critical}
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="neutral"
              stackId="drift"
              fill={colors.severity.medium}
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="positive"
              stackId="drift"
              fill={colors.severity.low}
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="trend-chart-legend">
        <span className="legend-item">
          <span className="legend-dot" style={{ background: colors.severity.critical }}></span>
          Negative
        </span>
        <span className="legend-item">
          <span className="legend-dot" style={{ background: colors.severity.medium }}></span>
          Neutral
        </span>
        <span className="legend-item">
          <span className="legend-dot" style={{ background: colors.severity.low }}></span>
          Positive
        </span>
      </div>
    </div>
  );
}

export default DriftActivityChart;
